/**
 * What a container's card and its row share under the head (20/10/2026):
 * one block per group (today / next / burned rows, verdict badge, paused
 * band, Reset pool — GroupLiveRows), the gateway blocks, hits, the
 * experiment, what auto-send held, and the footer with Edit container.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import type { DropGenGatewayVerdict, DropGenGroupVerdict, DropGenHitReport } from '@trafficpulse/shared';

import type { DayPlanContainerRow } from '../../../drop-generator.types';
import { instantText } from '../../../utils/clock/dk-label';
import { ExperimentLine } from '../../shared/experiment/ExperimentLine';
import { HitsLine } from '../../shared/hits/HitsLine';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { GatewayBlocksLine } from '../controls/GatewayBlocksLine';
import type { SimulateRequest } from '../controls/SimulateControl';
import { GroupLiveRows } from '../GroupLiveRows';
import { type ContainerLiveFacts, heldDrops } from '../live-status';
import { type CapCuts, capCutWords, NO_CUTS } from '../../shared/cap/cap-cuts';

export interface ContainerLiveActions {
  onEdit?: (containerId: string) => void;
  onResetPool?: (containerId: string, listGroupId: string) => void;
  resetting?: boolean;
  /** DEV ONLY — present only when the backend simulation is on. */
  onSimulate?: (containerId: string, listGroupId: string, req: SimulateRequest) => void;
  simulating?: boolean;
}

export interface ContainerLiveReport {
  verdicts?: DropGenGroupVerdict[];
  /** Phase 17 — this container's gateways as today's checks judged them. */
  gateways?: DropGenGatewayVerdict[];
  /** Phase 38 — this container's hits today (and what was held). */
  hits?: DropGenHitReport | null;
  /** 20/10/2026 — the slots the daily SMS cap cut or clamped today. */
  cuts?: CapCuts;
}

/** 19/10/2026 — "one list per group per day", unless a group sends to several today. */
export const listsLine = (container: DayPlanContainerRow): string => {
  const several = container.groups.filter((g) => (g.listsToday?.length ?? 0) > 1);
  if (several.length === 0) return 'one list per group per day';
  return several.map((g) => `${g.groupName}: ${g.listsToday?.length} lists today`).join(' · ');
};

const footerWords = (facts: ContainerLiveFacts, container: DayPlanContainerRow): string =>
  facts.stoppedToday > 0
    ? `${facts.stoppedToday} drop(s) stopped by a verdict today`
    : facts.skippedToday > 0
      ? `${facts.skippedToday} slot(s) skipped by hand today`
      : listsLine(container);

type Props = ContainerLiveActions & ContainerLiveReport & { container: DayPlanContainerRow; facts: ContainerLiveFacts };

export const ContainerLiveBody: FC<Props> = ({ container, facts, verdicts, gateways, hits, cuts = NO_CUTS, onEdit, onResetPool, resetting, onSimulate, simulating }) => {
  const held = heldDrops(container).slice(0, 3);
  const clock = useCountryClock(); // Danish first, the country's time after (15/10/2026)
  const { containerId } = container;
  return (
    <>
      <div className="flex flex-col gap-2">
        {container.groups.map((group) => (
          <GroupLiveRows
            key={group.listGroupId}
            container={container}
            group={group}
            verdict={verdicts?.find((v) => v.listGroupId === group.listGroupId)}
            listVerdicts={verdicts?.filter((v) => v.listGroupId === group.listGroupId && v.perList)}
            onResetPool={onResetPool ? () => onResetPool(containerId, group.listGroupId) : undefined}
            resetting={resetting}
            onSimulate={onSimulate ? (req) => onSimulate(containerId, group.listGroupId, req) : undefined}
            simulating={simulating}
            generated={facts.state !== 'not-generated'}
          />
        ))}
      </div>
      <GatewayBlocksLine gateways={gateways} />
      <HitsLine hits={hits} />
      <ExperimentLine experiment={container.experiment} />
      {held.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-warning">
          {held.map((h) => (
            <li key={h.atUtc} className="flex gap-1.5">
              <span className="whitespace-nowrap font-mono tabular-nums">{instantText(h.atUtc, clock)}</span>
              <span className="truncate">{h.note}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{footerWords(facts, container)}</span>
        {capCutWords(cuts) ? <span className="text-warning" title="The campaign group's daily SMS cap was spent before these slots — see Drop slots in the editor">· {capCutWords(cuts)}</span> : null}
        <span className="grow" />
        {onEdit ? (
          <button type="button" onClick={() => onEdit(containerId)} className="font-medium text-primary hover:underline">
            Edit container
          </button>
        ) : null}
      </div>
    </>
  );
};
